import { lastValueFrom } from "rxjs";
import { MemberBackend } from "src/app/backend/api/member.backend";
import { UserBackend } from "src/app/backend/api/user.backend";
import { HealthRecord } from "src/app/backend/models/health-record";
import { MemberLight } from "src/app/backend/models/member/member";
import { ReportDesignItemConfig } from "src/app/modules/bootstrap/modules/report/report-design-item";
import { compact } from "lodash-es";

export type ReportSignatureUserConfig = string | undefined;

export function normalizeMemberIds(staff: unknown): string[] {
  if (!staff) {
    return [];
  }

  if (!Array.isArray(staff)) {
    return [];
  }

  return compact(
    staff.map((entry: unknown) => {
      if (typeof entry === "string") {
        return entry;
      }

      if (entry && typeof entry === "object" && "id" in entry) {
        const id = (entry as { id: unknown }).id;

        return typeof id === "string" ? id : undefined;
      }

      return undefined;
    })
  );
}

function readSoapSummaryStaffRaw(item: ReportDesignItemConfig): unknown {
  const configStaff = (item.config as { staff?: unknown })?.staff;
  if (configStaff !== undefined && configStaff !== null) {
    return configStaff;
  }

  const baseConfig = (item as { baseConfig?: { staff?: unknown } }).baseConfig;

  return baseConfig?.staff;
}

export function soapSummaryStaffMemberIds(
  item: ReportDesignItemConfig
): string[] {
  return normalizeMemberIds(readSoapSummaryStaffRaw(item));
}

export function findSoapSummaryAuthorMemberIds(
  items: ReportDesignItemConfig[]
): string[] | undefined {
  for (const item of items) {
    if (item.name === "soap-summary" && item.type === "custom") {
      const ids = soapSummaryStaffMemberIds(item);

      if (ids.length > 0) {
        return ids;
      }

      continue;
    }

    const nested = findSoapSummaryAuthorMemberIdsInNestedItem(item);
    if (nested !== undefined) {
      return nested;
    }
  }

  return undefined;
}

function findSoapSummaryAuthorMemberIdsInNestedItem(
  item: ReportDesignItemConfig
): string[] | undefined {
  switch (item.type) {
    case "group":
    case "group-vertical":
    case "group-horizontal": {
      const group = (item.config as { group?: ReportDesignItemConfig[] })
        ?.group;

      return group ? findSoapSummaryAuthorMemberIds(group) : undefined;
    }
    case "tab": {
      const tabs = (
        item.config as { tabs?: { items: ReportDesignItemConfig[] }[] }
      )?.tabs;

      if (!tabs) {
        return undefined;
      }

      for (const tab of tabs) {
        const found = findSoapSummaryAuthorMemberIds(tab.items || []);

        if (found !== undefined) {
          return found;
        }
      }

      return undefined;
    }
    default:
      return undefined;
  }
}

export function resolveAuthorMemberIdsForLiveForm(params: {
  healthRecord?: HealthRecord | undefined;
  dataSource?: Record<string, unknown> | undefined;
  liveTemplateItems: ReportDesignItemConfig[];
}): string[] {
  const fromSoapSummary = findSoapSummaryAuthorMemberIds(
    params.liveTemplateItems
  );

  if (fromSoapSummary !== undefined) {
    return fromSoapSummary;
  }

  return resolveAuthorMemberIds({
    healthRecord: params.healthRecord,
    dataSource: params.dataSource,
    rootTemplate: params.liveTemplateItems
  });
}

export function buildSignatureAuthorMemberIdsKey(memberIds: string[]): string {
  return `authors:${memberIds.join(",")}`;
}

export function parseSignatureAuthorMemberIdsKey(key: string): string[] {
  if (!key.startsWith("authors:")) {
    return [];
  }

  const rest = key.slice("authors:".length);

  if (!rest) {
    return [];
  }

  return rest.split(",").filter((id) => id.length > 0);
}

export function extractSoapTemplateStaffIdsFromTemplate(
  items: ReportDesignItemConfig[]
): string[] {
  const ids: string[] = [];

  for (const item of items) {
    if (item.name === "soap-summary" && item.type === "custom") {
      ids.push(...soapSummaryStaffMemberIds(item));
    }

    collectStaffIdsFromNestedItems(item, ids);
  }

  return ids;
}

function collectStaffIdsFromNestedItems(
  item: ReportDesignItemConfig,
  ids: string[]
): void {
  switch (item.type) {
    case "group":
    case "group-vertical":
    case "group-horizontal": {
      const group = (item.config as { group?: ReportDesignItemConfig[] })
        ?.group;
      if (group) {
        for (const child of group) {
          if (child.name === "soap-summary" && child.type === "custom") {
            ids.push(...soapSummaryStaffMemberIds(child));
          }
          collectStaffIdsFromNestedItems(child, ids);
        }
      }
      break;
    }
    case "tab": {
      const tabs = (
        item.config as { tabs?: { items: ReportDesignItemConfig[] }[] }
      )?.tabs;
      if (tabs) {
        for (const tab of tabs) {
          for (const child of tab.items || []) {
            if (child.name === "soap-summary" && child.type === "custom") {
              ids.push(...soapSummaryStaffMemberIds(child));
            }
            collectStaffIdsFromNestedItems(child, ids);
          }
        }
      }
      break;
    }
    default:
      break;
  }
}

export function resolveAuthorMemberIds(params: {
  healthRecord?: HealthRecord | undefined;
  dataSource?: Record<string, unknown> | undefined;
  rootTemplate?: ReportDesignItemConfig[];
}): string[] {
  const fromHealthRecord = normalizeMemberIds(params.healthRecord?.staff);
  if (fromHealthRecord.length > 0) {
    return fromHealthRecord;
  }

  const fromAuthors = normalizeMemberIds(params.dataSource?.authors);
  if (fromAuthors.length > 0) {
    return fromAuthors;
  }

  const fromStaff = normalizeMemberIds(params.dataSource?.staff);
  if (fromStaff.length > 0) {
    return fromStaff;
  }

  if (params.rootTemplate) {
    return extractSoapTemplateStaffIdsFromTemplate(params.rootTemplate);
  }

  return [];
}

async function loadMemberSignatureHtml(
  memberBackend: MemberBackend,
  memberId: string
): Promise<string> {
  const memberLight: MemberLight = await lastValueFrom(
    memberBackend.loadLight(memberId)
  );

  if (memberLight.signature) {
    return memberLight.signature;
  }

  try {
    const member = await lastValueFrom(memberBackend.load(memberId));

    return member.user?.signature || "";
  } catch {
    return "";
  }
}

export async function loadSignatureHtmlListForConfig(params: {
  userConfig: ReportSignatureUserConfig;
  memberBackend: MemberBackend;
  userBackend: UserBackend;
  healthRecord?: HealthRecord | undefined;
  dataSource?: Record<string, unknown> | undefined;
  rootTemplate?: ReportDesignItemConfig[];
}): Promise<string[]> {
  const userConfig = params.userConfig;

  if (!userConfig) {
    return [];
  }

  switch (userConfig) {
    case "authors": {
      const fromDataSourceAuthors = normalizeMemberIds(
        params.dataSource?.authors
      );
      const authorIds =
        fromDataSourceAuthors.length > 0
          ? fromDataSourceAuthors
          : resolveAuthorMemberIds({
              healthRecord: params.healthRecord,
              dataSource: params.dataSource,
              rootTemplate: params.rootTemplate
            });

      return compact(
        await Promise.all(
          authorIds.map((memberId) =>
            loadMemberSignatureHtml(params.memberBackend, memberId)
          )
        )
      );
    }
    case "current-user": {
      const user = await lastValueFrom(params.userBackend.loadMe());

      return compact([user.signature || ""]);
    }
    default: {
      const html = await loadMemberSignatureHtml(
        params.memberBackend,
        userConfig
      );

      return compact([html]);
    }
  }
}
